/**
 * ホストとサンドボックスの間で運ぶファイルの詰め方（単一定義）
 *
 * ## 形は tar に合わせる
 *
 * ローカル実行先は Docker の `getArchive` / `putArchive`（tar）で運ぶ。その意味論は
 *
 * - 詰めた項目のパスは**転送元の親からの相対パス**（＝先頭が転送元の basename）
 * - 展開先は**宛先パスの親ディレクトリ**
 *
 * である。したがって `copyToContainer('/host/a.txt', '/box/dir/b.txt')` は
 * `/box/dir/a.txt` を作る（宛先の basename は使われない）。直感的ではないが、
 * **クラウド実行先が別の意味論を採ると同じ呼び出しが実行先で違う結果になる**。
 * 呼び出し側（撮影・提出・スキル配置）は既にこの意味論に依存しているため、
 * ここを合わせることが最優先である。
 *
 * ## なぜ tar そのものではないのか
 *
 * 両端で使える保証が要る。サンドボックス側は Debian なので `tar` があるが、
 * **ホストは利用者のパソコン**であり Windows も含む。外部コマンドに頼ると、
 * 動く端末と動かない端末が生まれる。JSON + base64 なら Node だけで完結する。
 *
 * base64 は約 33% 膨らむが、この経路には転送前の上限判定（`sandbox-transfer`）が
 * あり、運ぶ量はもともと小さく抑えられている。
 *
 * ## 保つのは中身と実行ビットだけ
 *
 * 所有者・時刻・パーミッション全体は再現しない。**再現しないと決めておくほうが、
 * 実行先によって再現度が変わるより扱いやすい。** 実行ビットだけは、スクリプトを
 * 運んだときに動かなくなるため保つ。
 */
import { z } from 'zod';
/**
 * アーカイブ内の 1 件。
 *
 * `path` は**転送元の親からの相対パス**（POSIX 区切り）。単一ファイル
 * `/host/a.txt` なら `a.txt`、ディレクトリ `/host/dir` の中身なら
 * `dir/sub/b.txt` のようになる。
 */
export const SandboxArchiveEntrySchema = z.object({
    path: z.string().min(1),
    /** 内容（base64） */
    content: z.string(),
    /** 実行ビット。付いていない場合は false 相当 */
    executable: z.boolean().optional(),
});
export const SandboxArchiveSchema = z.object({
    entries: z.array(SandboxArchiveEntrySchema),
    /**
     * 転送元がディレクトリだったときの、その名前。
     *
     * 中身が空のディレクトリを運ぶと項目が 1 件も無く、宛先に何を作ればよいか
     * 分からなくなる。名前を持たせて、空でも宛先を作れるようにする
     */
    directoryName: z.string().min(1).optional(),
    /**
     * 転送元の配下にあった、中身が空のディレクトリの相対パス（転送元の親から）。
     *
     * **項目が 1 件も無いディレクトリは、記録しないと運ばれない。** ローカル実行先の
     * tar は空のディレクトリをそのまま残すため、ここを落とすと同じ操作が実行先で
     * 違う結果になる（例: `skill/outputs/` が消え、あとでそこへ書く処理が ENOENT）。
     * `directoryName` が転送元そのものを指すのに対し、こちらは**その配下**を指す
     */
    directories: z.array(z.string().min(1)).optional(),
});
/**
 * 展開先の相対パスとして安全か。
 *
 * **展開する側が必ず確かめる。** アーカイブは相手側で作られた値であり、
 * `../` を含む相対パスをそのまま結合すると、展開先の外へ書き込める。
 */
export function isSafeArchiveEntryPath(entryPath) {
    if (entryPath.length === 0)
        return false;
    // 絶対パス・ドライブレター・UNC を拒否する
    if (entryPath.startsWith('/') || entryPath.startsWith('\\'))
        return false;
    if (/^[A-Za-z]:/.test(entryPath))
        return false;
    const segments = entryPath.split(/[/\\]/);
    return segments.every((segment) => segment !== '' && segment !== '.' && segment !== '..');
}
/**
 * 展開前に、結合するパスがすべて安全か確かめる。
 *
 * **`directoryName` と `directories` も同じ検査を通す。** 項目のパスだけ確かめても、
 * 空ディレクトリ用に持たせたこれらを素通しで結合すれば展開先の外にディレクトリを
 * 作れてしまう。
 * 展開する側は両端（ホスト・サンドボックス）にあるため、検査もここに 1 つだけ置く。
 *
 * 安全でないものが 1 件でもあれば、何も書く前に失敗させる。
 */
export function assertSafeArchivePaths(archive) {
    const unsafe = archive.entries.find((entry) => !isSafeArchiveEntryPath(entry.path));
    if (unsafe) {
        throw new Error(`Archive contains an unsafe entry path: ${unsafe.path}`);
    }
    if (archive.directoryName !== undefined && !isSafeArchiveEntryPath(archive.directoryName)) {
        throw new Error(`Archive contains an unsafe directory name: ${archive.directoryName}`);
    }
    const unsafeDirectory = archive.directories?.find((dir) => !isSafeArchiveEntryPath(dir));
    if (unsafeDirectory !== undefined) {
        throw new Error(`Archive contains an unsafe directory path: ${unsafeDirectory}`);
    }
}
//# sourceMappingURL=sandbox-archive.js.map